import { Skeleton } from '@/shared/components/ui/skeleton';
import { ErrorState } from '../../analytics/shared/ErrorState';
import { useReferralFeedbackProviderDetail } from '../../../hooks/useReferralFeedbackData';
import type {
  ReferralFeedbackProviderReferral,
  ReferralSubjectType,
} from '../../../types/engagement-metrics.types';

const SOURCE_BADGE: Record<ReferralSubjectType, { label: string; text: string; bg: string }> = {
  PROVIDER_REFERRAL: { label: 'FA referral', text: '#2c6e7d', bg: '#e2edf0' },
  SP_PROVIDER_REFERRAL: { label: 'SP referral', text: '#2c5c47', bg: '#e4ede8' },
  MANUAL_PROFESSIONAL_REFERRAL: { label: 'Manual', text: '#87702f', bg: '#f3edd9' },
  CONSULTATION_REQUEST: { label: 'Call request', text: '#46584f', bg: '#edeae3' },
};

const CONVERTED_LABELS: Record<string, string> = {
  YES: 'Converted',
  NOT_YET: 'Not yet',
  NO: 'Did not convert',
};

const dash = '—';
const yesNo = (value: boolean | null): string => (value == null ? dash : value ? 'Yes' : 'No');
const ratingText = (value: number | null): string => (value != null ? value.toFixed(1) : dash);

const SourceBadge = ({ subjectType }: { subjectType: ReferralSubjectType }) => {
  const badge = SOURCE_BADGE[subjectType] ?? {
    label: subjectType,
    text: '#7f8a83',
    bg: '#f0ede6',
  };
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[.3px]"
      style={{ color: badge.text, backgroundColor: badge.bg }}
    >
      {badge.label}
    </span>
  );
};

const AnswerPair = ({ label, value }: { label: string; value: string }) => (
  <span className="text-[12px]" style={{ color: '#46584f' }}>
    <span className="font-bold uppercase text-[10.5px] tracking-[.3px]" style={{ color: '#7f8a83' }}>
      {label}
    </span>{' '}
    {value}
  </span>
);

const ReferralRow = ({ referral }: { referral: ReferralFeedbackProviderReferral }) => (
  <li
    className="border-b last:border-0 py-3 first:pt-1 last:pb-1 space-y-1.5"
    style={{ borderColor: '#edeae3' }}
    data-testid="referral-detail-row"
  >
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <SourceBadge subjectType={referral.subjectType} />
      <span className="text-[12.5px] font-semibold" style={{ color: '#1b2a23' }}>
        {new Date(referral.referredAt).toLocaleDateString()}
      </span>
      <span className="text-[11px] font-bold uppercase tracking-[.3px]" style={{ color: '#7f8a83' }}>
        {referral.followupStatus.replace(/_/g, ' ')}
      </span>
    </div>
    <p className="text-[12.5px]" style={{ color: '#46584f' }}>
      Referred by {referral.referrerName ?? dash} · Care seeker {referral.careSeekerName ?? dash}
      {referral.clientNameWithheld && (
        <span className="text-[11px] italic" style={{ color: '#7f8a83' }}>
          {' '}
          (name withheld from SP)
        </span>
      )}
    </p>
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      <AnswerPair label="SP reached" value={yesNo(referral.spReached)} />
      <AnswerPair label="SP rating" value={ratingText(referral.spRating)} />
      <AnswerPair
        label="SP converted"
        value={(referral.spConverted && CONVERTED_LABELS[referral.spConverted]) || dash}
      />
      <AnswerPair label="CS reached" value={yesNo(referral.csReached)} />
      <AnswerPair label="CS rating" value={ratingText(referral.csRating)} />
    </div>
    {referral.spReason && (
      <p className="text-[12px] italic" style={{ color: '#46584f' }}>
        “{referral.spReason}”
      </p>
    )}
  </li>
);

/**
 * Inline drill-down rendered inside an expanded provider row: every
 * follow-up-eligible platform referral for that SP, newest first (the backend
 * scopes this to platform-initiated referrals — call requests live in the CS
 * table's drill-down). Mounted only on expand, so the query fires lazily.
 */
export const ReferralFeedbackProviderDetailPanel = ({ spUserId }: { spUserId: string }) => {
  const { data, isLoading, isError, refetch } = useReferralFeedbackProviderDetail(spUserId);

  if (isError) {
    return (
      <ErrorState
        message="Failed to load this provider's referrals. Please try again."
        onRetry={() => refetch()}
      />
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-2 py-2" data-testid="referral-detail-loading">
        <Skeleton className="h-14 w-full rounded-lg" />
        <Skeleton className="h-14 w-full rounded-lg" />
      </div>
    );
  }

  if (data.referrals.length === 0) {
    return (
      <p className="py-2 text-sm text-gray-500">No referrals for this provider yet.</p>
    );
  }

  return <ul>{data.referrals.map((r) => <ReferralRow key={`${r.subjectType}-${r.subjectId}`} referral={r} />)}</ul>;
};
